import Color from 'color';
import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { getTopLeft } from 'ol/extent';
import { Geometry, LineString, Point } from 'ol/geom';
import {
  Style,
  Fill,
  Stroke,
  Circle as CircleStyle,
  Text,
  RegularShape,
} from 'ol/style';
import { TextPlacement } from 'ol/style/Text';

import { error, warn } from '@/utils/logger';

import { FIELD_MP } from '../types';

import {
  FITTING_EC,
  FITTING_STO,
  LENGTH_DG,
  DEFAULT_DIAMOND_RADIUS,
  DEFAULT_EQUIPMENT_FONT_SIZE as DEF,
  DEFAULT_TRIANGLE_RADIUS,
  COMMON_STROKE_WIDTH as CSW,
  DEFAULT_NOTE_TXT,
  DEFAULT_EC_TXT as EC,
  DEFAULT_DIGN_TXT as DG,
  DEFAULT_TRIANGLE_RADIUS as DTR,
  DEFAULT_DIAMOND_RADIUS as DDR,
  scaleRatio,
  ZOOM_LV,
} from './config';
import { PAL } from './Palette';
import {
  composeFont,
  strokeWidthByZoom,
  zoomLevelToFontSize,
  hasMultiplierInEqmt,
  isEquipmentFeature,
  isOffsetFeature,
} from './styleUtils';

/**
 * Diamond style build function with color and zoom level
 *
 * @param color
 * @param zoomLevel
 * @returns
 */
export const buildDiamondStyleWith = (color: string, zoomLevel?: number) => {
  const roundZoomLevel = zoomLevel || 0;
  const tRadius = roundZoomLevel ? roundZoomLevel * scaleRatio : DDR;
  const thickener = strokeWidthByZoom(zoomLevel);
  const noMoreThan2 = Math.min(thickener, 2);
  return basicDiamonStyle(color, noMoreThan2, tRadius);
};
/**
 * Check feature flag to add EC and diagonal style
 * TODO: move diagonal style into a separate function!
 * @param feature polygon to attach child style: ec, diagonal
 * @param origStyle
 * @returns
 */
export const buildPolygonWithECStyle = (feature: Feature, origStyle: Style) => {
  const hasEC = feature.get(FITTING_EC);
  const hasDiagonal = feature.get(LENGTH_DG);
  const hasSTO = feature.get(FITTING_STO);

  const extras: Style[] = [];
  if (hasDiagonal) extras.push(buildDiagonalLineStyle());
  if (hasEC) extras.push(basicECStyle(EC));
  if (hasSTO) extras.push(...buildSideTakeoffCirclesStyle());

  if (extras.length > 0) return [origStyle, ...extras];
  return origStyle;
};

/**
 * Build one circle style at the left midpoint (horizontal duct) or top midpoint
 * (vertical duct) of a DUCT_LENGTH rect, used to visualize an attached
 * SIDE_TAKEOFF symbol - @2026/05/28
 */
export const buildSideTakeoffCirclesStyle = (): Style[] => {
  const fillColor = 'rgba(0, 0, 0, 0.1)';
  const strokeColor = 'green';
  const dotPoint = (extent: number[]): number[] => {
    const [minX, minY, maxX, maxY] = extent;
    const isVertical = maxY - minY > maxX - minX;
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    return isVertical ? [midX, maxY] : [minX, midY];
  };

  const circle = new Style({
    geometry: (feature) => {
      const geom = feature.getGeometry();
      if (!geom) return undefined;
      return new Point(dotPoint(geom.getExtent()));
    },
    renderer: (coords, state) => {
      const geom = state.feature.getGeometry();
      if (!geom) return;
      const [minX, minY, maxX, maxY] = geom.getExtent();
      const shortSidePx = Math.min(maxX - minX, maxY - minY) / state.resolution;
      const r = Math.max(4, shortSidePx * 0.6);
      const ctx = state.context;
      const [cx, cy] = coords as number[];
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, 2 * Math.PI);
      ctx.fillStyle = fillColor;
      ctx.fill();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = CSW + r / 6;
      ctx.stroke();
    },
  });

  return [circle];
};

/**
 * Equipment tag style with multiplier support
 *
 * @date 2025/08/06
 * @log add multiplier 2025/10/17
 *
 * @param label
 * @param color
 * @param zoomLevel
 * @param activate
 * @param multiplier superscript: x?
 *
 * @returns text style
 */
export const buildEquipmentStyleWith = (
  label: string,
  color: string,
  zoomLevel: number,
  activate?: boolean,
  multiplier?: string,
) => {
  const validFontSizeByZoomLevel = zoomLevelToFontSize(zoomLevel, 0.8);
  return buildComplexTextStyle(
    label,
    multiplier,
    validFontSizeByZoomLevel,
    color,
    activate,
  );
};

/**
 * Triangle style build function with color and zoom level
 *
 * @param color
 * @param zoomLevel
 * @returns
 */
export const buildTriangleStyleWith = (color: string, zoomLevel?: number) => {
  const roundZoomLevel = zoomLevel || 0;
  const tRadius = roundZoomLevel ? Math.pow(roundZoomLevel, 2) * 0.7 : DTR;
  const thickener = strokeWidthByZoom(zoomLevel);
  const noMoreThan2 = Math.min(thickener, 2);
  return basicTriangleStyle(color, noMoreThan2, tRadius);
};

/**
 * Create line style with palette color `PAL.UAL`
 * @date 2024/03/21
 *
 * @param color color string
 * @param stroke stroke size
 * @param withDash optional
 * @returns
 */
export const basiceLengthStyle = (
  color: string = PAL.UAL,
  strokeWidth?: number,
  withDash?: boolean,
): Style => {
  const stroke = new Stroke({
    color,
    width: strokeWidth || CSW,
  });
  if (withDash) {
    stroke.setLineDash([6, 2]);
  }
  return new Style({
    geometry: function (feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
    },
    stroke,
  });
};

export const basicPolygonStyle = (
  active = false,
  strokeColor = 'blue',
  strokeWidth = 1,
): Style => {
  return new Style({
    /**
     * NOTE: Remember to keep this `geometry` property!
     * It will allow the rectangle symbol to resize in a rectanglar shape!
     * Once removed this part then it caused a bug:
     * https://taksoai.atlassian.net/browse/TAK-501
     *
     * @param feature
     * @returns
     */
    geometry: function (feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
    },
    fill: new Fill({
      color: 'rgba(0, 0, 0, 0.01)', // transparent black
    }),
    stroke: new Stroke({
      color: active ? '#3F99F7' : strokeColor, // sky blue line
      width: strokeWidth,
    }),
  });
};

export const basicCircleStyle = (
  strokeColor = 'green',
  strokeWidth = 2,
): Style => {
  return new Style({
    stroke: new Stroke({
      color: strokeColor,
      width: strokeWidth,
    }),
    fill: new Fill({
      color: 'green',
    }),
  });
};

/**
 * build a text object for feature style
 * @param label text
 * @param size font size
 * @param italic
 * @param placement
 * @param isRichFormat
 * @param isActive for highlight style
 * @param color for text color, green by defaut
 *
 * @returns
 */
export const basicTextStyle = (
  label: string,
  size?: number,
  italic?: boolean,
  placement: TextPlacement = 'point',
  isRichFormat = false,
  isActive = false,
  color = 'green',
  offsetY = 0,
): Text => {
  const fontSize = size || DEF;
  const options = {
    font: composeFont(fontSize, italic),
    placement,
    fill: new Fill({
      color,
    }),
    offsetX: 0,
    offsetY,
    overflow: true, // allowing text overlap, and display more
    stroke: new Stroke({
      color: 'white',
      width: 1,
    }),
    text: label || 'MOCK',
    textAlign: 'left',
  } as any;
  // more format to create a box,
  const richOpts = {
    // ** final text color, will override the `fill` attribute in `options`!
    fill: new Fill({
      color: 'coral',
    }),
    // override the default options
    placement: 'point' as TextPlacement,
    // == box border color: ==
    // ** available while placement is point only
    backgroundStroke: new Stroke({
      color: isActive ? color : 'orange',
      width: 1,
    }),
    // available while placement is point only
    backgroundFill: new Fill({
      color: isActive ? [255, 0, 0, 0.1] : [255, 255, 255, 0.1],
    }),
    padding: [1, 1, 1, 1],
    // NOTE: keep text algin left to make text looks stable
    // @2024/04/29, @2025/10/17
    textAlign: 'left',
    textBaseline: 'top',
  };
  const endOptions = isRichFormat ? { ...options, ...richOpts } : options;
  return new Text(endOptions);
};

export const basicECStyle = (ecTxt: string) => {
  return new Style({
    text: basicTextStyle(ecTxt, 10, false, 'point', false, false, 'red'),
  });
};

export const basicTriangleStyle = (
  strokeColor = 'blue',
  strokeWidth = 2,
  radius = DEFAULT_TRIANGLE_RADIUS,
): Style => {
  const stroke = new Stroke({ color: strokeColor, width: strokeWidth });
  const fill = new Fill({ color: 'rgba(255, 0, 0, 0.01)' });
  return new Style({
    image: new RegularShape({
      fill: fill,
      stroke: stroke,
      points: 3,
      radius,
      rotation: Math.PI / 4,
      angle: 0,
    }),
  });
};

export const basicDiamonStyle = (
  strokeColor = 'blue',
  strokeWidth = 2,
  radius = DEFAULT_DIAMOND_RADIUS,
): Style => {
  const stroke = new Stroke({ color: strokeColor, width: strokeWidth });
  const fill = new Fill({ color: 'rgba(255, 0, 0, 0.01)' });
  return new Style({
    image: new RegularShape({
      fill: fill,
      stroke: stroke,
      points: 4,
      radius,
      angle: 0,
    }),
  });
};

/**
 * Make a complext text renderer function for equipment `multiplier` display.
 *
 * @param label equipment text
 * @param multiplier right-top corner text: x?
 * @param fontSize font size
 * @param fontColor font color
 * @param isActive if highligt
 */
export const buildComplexTextStyle = (
  label: string,
  multiplier: string = '',
  fontSize: number,
  fontColor: string,
  isActive: boolean | undefined,
) => {
  // ! This radio(0.5) can make selected equipment text size looks normal!
  const seletedSizeChangeRatio = 0.5;
  return new Style({
    // ! Need to keep simple text style to make point feature have selected style
    text: basicTextStyle(
      label,
      fontSize * seletedSizeChangeRatio,
      false,
      'point',
      true,
      true,
      'green',
      0, // must be `0`, otherwise the text box have wierd offset!
    ),
    // ! OL hit detection paints this fill into an offscreen canvas and does
    // pixel colour lookup. Without a Fill the Polygon won't appear there and
    // the feature is never detected. The renderer below overrides the visual,
    // so this colour never shows on screen. @2026/05/29
    fill: new Fill({ color: 'rgba(0, 0, 0, 1)' }),
    renderer: function (coordinates, state) {
      // Geometry is now a Polygon; coordinates[0][0] is the top-left pixel of
      // the label box (matching the startX/startY from feature creation).
      const [startX, startY] = (coordinates as Coordinate[][])[0][0];
      const { context: ctx } = state;
      ctx.save();
      ctx.font = composeFont(fontSize);
      const spaceTotal = 12;
      const metrics = ctx.measureText(label);
      const boxWidth = metrics.width + spaceTotal;
      const boxHeight = fontSize + spaceTotal;

      // Draw background fill
      ctx.fillStyle = isActive
        ? `rgba(255, 0, 0, 0.1)`
        : `rgba(255, 255, 255, 0.1)`;
      ctx.fillRect(startX, startY, boxWidth, boxHeight);

      // Draw border
      ctx.lineWidth = 1;
      ctx.strokeStyle = isActive ? 'green' : 'orange';
      ctx.strokeRect(startX, startY, boxWidth, boxHeight);

      // Text anchor is spaceTotal/2 inside the box top-left
      const x0 = startX + spaceTotal / 2;
      const y0 = startY + spaceTotal / 2;
      ctx.fillStyle = fontColor;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(label, x0, y0);

      // Draw top-right corner multiplier superscript if present
      if (multiplier) {
        const thrink = 0.8;
        ctx.fillStyle = 'red';
        ctx.font = composeFont(fontSize * thrink);
        ctx.fillText(
          `x${multiplier}`,
          x0 + metrics.width,
          y0 - fontSize * thrink,
        );
      }

      ctx.restore();
    },
  });
};

const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  /** fontSize*1.5 */
  lineHeight: number,
) => {
  const words = text.split(' ');
  let line = '';

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && n > 0) {
      ctx.fillText(line, x, y);
      line = words[n] + ' ';
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, y);
};

/**
 * Custom note style builder based on renderer function
 * @param bgColor
 * @returns
 */
export const baseNoteRenderStyle = (
  bgColor: string = 'orange',
  text: string = DEFAULT_NOTE_TXT,
  fontSize: number = 14,
) => {
  const color = Color(bgColor);
  const red = color.red();
  const green = color.green();
  const blue = color.blue();
  const opacity = 0.5;
  return new Style({
    renderer: function (coordinates, state) {
      const [[tl, tr, br, bl]] = coordinates as Coordinate[][];
      const { context: ctx } = state;
      const [x0, y0] = tl;
      const width = tr[0] - tl[0];
      const height = bl[1] - tr[1];
      ctx.save();

      // Draw a filled rectangle
      ctx.fillStyle = `rgba(${red}, ${green}, ${blue}, ${opacity})`;
      // Draw a blue filled square (x, y, width, height)
      ctx.fillRect(x0, y0, width, height);

      // Draw an outlined rectangle
      ctx.strokeStyle = 'green'; // Set the stroke color
      // default boder size 1px
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y0, width, height);

      // draw text with `black` color
      ctx.font = composeFont(fontSize);
      ctx.fillStyle = 'black';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      // ctx.fillText(text, x0 + 8, y0 + 8);
      wrapText(ctx, text, x0 + 4, y0 + 4, width - 4, fontSize * 1.4);

      ctx.restore();
    },
  });
};

/**
 * Set feature style to override the style defined in Vector Layer
 * Simplified style only for highlighted feature use!
 *
 * @param strokeColor
 * @param strokeWidth
 * @param fillColor undefined by default
 * @returns
 */
export const buildHighlightFeatureStyle = (
  strokeColor = 'blue',
  strokeWidth = 1,
  fillColor?: string,
): Style => {
  return new Style({
    stroke: new Stroke({
      color: strokeColor,
      width: strokeWidth,
    }),
    fill: new Fill({
      color: fillColor || 'rgba(0, 0, 255, 0.1)',
    }),
  });
};

/**
 * For `activeFeature` in featureRender use
 * @param feature
 * @returns
 */
export const getActivePointStyle = (
  feature: Feature,
): Style | Style[] | undefined => {
  // FIXME: this could be null, no idea in which case?
  // @2024/07/23
  const origStyle = feature.getStyle();
  if (!origStyle) {
    error(`## got null orig style!`);
    return undefined;
  }

  // ! `origStyle` could be an array includes two style object
  // @2025/12/10
  if (origStyle instanceof Array) {
    // warn(`## got an array style(could be equipment with multiplier), use origStyle!`);
    return origStyle;
  }

  const styleObj = origStyle as Style;
  const origText = styleObj.getText();

  // == FOR: triangle or diamond symbol ==
  if (!origText) {
    const image = styleObj.getImage() as RegularShape;
    if (!image) {
      warn(`## No image shape return!`);
      return styleObj;
    }
    const fill = new Fill({ color: 'rgba(255, 0, 0, 0.6)' });
    const stroke = new Stroke({ color: 'red', width: 2 });
    image.setFill(fill);
    image.setStroke(stroke);
    return new Style({ image });
  }
  const redCenter = new CircleStyle({
    radius: 6, // smaller for dot circle feature @2022/11/30
    fill: new Fill({
      color: '#ff3333', // red center
    }),
  });
  // FIXME: fetch zoom level to reset equipment
  // @2026/03/06
  const zoomLevel = feature.get(ZOOM_LV);
  if (zoomLevel) {
    const realtimeFontSize = zoomLevelToFontSize(zoomLevel, 0.8);
    const realtimeText = composeFont(realtimeFontSize);
    origText.setFont(realtimeText);
  }
  // could be a polygon - @2026/05/29
  const geometry = feature.getGeometry() as Geometry;
  const highlightPointStyle = new Style({
    text: origText,
    image: redCenter,
    geometry: new Point(getTopLeft(geometry.getExtent())),
  });
  // == for equipment with multiplier setting ==
  // ! check if include `multiplier` text
  // @2025/12/09
  const canDrawMultplr = hasMultiplierInEqmt(feature);
  if (canDrawMultplr) {
    const multiplier = feature.get(FIELD_MP);
    const prefixMtpl = `x${multiplier}`;
    const text = basicTextStyle(
      prefixMtpl,
      10,
      false,
      'point',
      false,
      false,
      'green',
      -8, // move up
    );
    const mtplStyle = new Style({ text });
    return [highlightPointStyle, mtplStyle];
  }
  // ! keep the active equipment style: red-dot + text box
  // @2026/07/20
  const isEqmt = isEquipmentFeature(feature);
  if (isEqmt) {
    return highlightPointStyle;
  }
  // ! keep the diamond feature style: @2026/07/29
  const isOffset = isOffsetFeature(feature);
  if (isOffset) {
    return buildDiamondStyleWith('green', zoomLevel * 1.5);
  }

  // == to return selected triangle symbol style ==
  // @2026/07/07
  const triangleStyle = new Style({
    image: new RegularShape({
      fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' }),
      stroke: new Stroke({ color: 'red', width: 2 }),
      points: 3,
      radius: Math.pow(zoomLevel || 6, 2) * 0.8, // adjust to taste
      rotation: Math.PI / 4,
      angle: 0,
    }),
    geometry: new Point(getTopLeft(geometry.getExtent())),
  });
  return [triangleStyle, highlightPointStyle];
};

/**
 * Build a diagonal line style from top-left to bottom-right of a polygon feature.
 * Mirrors the `modifyGeometry` pattern so it tracks resizing correctly.
 *
 * @param strokeColor line color, defaults to blue
 */
export const buildDiagonalLineStyle = (strokeColor = 'blue'): Style => {
  return new Style({
    geometry: function (feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      const geom = modifyGeometry
        ? modifyGeometry.geometry
        : feature.getGeometry();
      const [minX, minY, maxX, maxY] = geom.getExtent();
      // [minX, maxY] = top-left, [maxX, minY] = bottom-right in OL negative-Y space
      return new LineString([
        [minX, maxY],
        [maxX, minY],
      ]);
    },
    stroke: new Stroke({ color: strokeColor, width: 1 }),
    text: basicTextStyle(DG, 12, false, 'line', false, false, 'red'),
  });
};

/**
 * rerender newly create feature with light green
 * @param strokeColor light green
 * @returns
 */
export const freshFeatureStyle = (strokeColor = '#03C04A'): Style => {
  const basicStyle = basicPolygonStyle(false, strokeColor, 2);
  basicStyle.setFill(
    new Fill({
      color: 'rgba(0, 255, 0, 0.5)', // translucent green
    }),
  );
  return basicStyle;
};
